import { Card, Paper, Stack, Title } from '@mantine/core'
import { ResourceTable } from '@medplum/react'
import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import type { SDRAction } from '@seen/core/models/tasks/sdr/sdr-action'
import { Button } from '@seen/ui'
import { IconArrowBackUp } from '@tabler/icons-react'
import { orderBy } from 'lodash-es'
import type { JSX } from 'react'
import { ActivityComment } from '#src/components/activity/ActivityComment.tsx'
import { ActivityList } from '#src/components/activity/ActivityList.tsx'
import { CommentComposer } from '#src/components/CommentComposer.tsx'
import { EditSDRActionState } from '#src/components/sdr/update/EditSDRActionState.tsx'
import { EditTaskDescription } from '#src/components/sdr/update/EditTaskDescription.tsx'
import { EditTaskDueDate } from '#src/components/sdr/update/EditTaskDueDate.tsx'
import { EditTaskOwner } from '#src/components/sdr/update/EditTaskOwner.tsx'
import { useSearch } from '#src/hooks/useSearch.ts'

export const SDRActionDisplay = ({
  sdr: sdrAction,
  onClickNavigate,
}: {
  sdr: SDRAction
  onClickNavigate: (sdrId: string) => void
}): JSX.Element => {
  const { data: comments = [] } = useSearch(
    CommentCommunication,
    CommentCommunication.searchQuery([sdrAction.reference])
  )

  const sortedComments = orderBy(comments, (c) => c.sent, ['asc'])

  return (
    <Paper>
      <Stack>
        <Title order={2}>Service Determination Request Action</Title>

        <Button
          onClick={() =>
            sdrAction.parentSdrId && onClickNavigate?.(sdrAction.parentSdrId)
          }
        >
          <IconArrowBackUp className="size-4" />
          Back to parent SDR
        </Button>

        <Card withBorder>
          <Title order={2}>{sdrAction.description}</Title>
          <ResourceTable
            value={sdrAction.toFhirResource()}
            ignoreMissingValues={true}
          />
        </Card>

        <Card withBorder>
          <Title order={3}>Update SDR Action State</Title>
          <EditSDRActionState sdrAction={sdrAction} />
        </Card>

        <Card withBorder>
          <Title order={3}>Edit SDR Action</Title>
          <Stack>
            <EditTaskOwner task={sdrAction} />
            <EditTaskDueDate task={sdrAction} />
            <EditTaskDescription sdrAction={sdrAction} />
          </Stack>
        </Card>

        <Card withBorder>
          <Title order={3}>Notes</Title>
          <div className="-mx-1.5">
            <CommentComposer
              partOf={[sdrAction.reference]}
              subject={sdrAction.participant}
            />
          </div>
          {sortedComments.length > 0 && (
            <ActivityList>
              {sortedComments.map((comment) => (
                <ActivityComment
                  key={comment.reference.reference}
                  comment={comment}
                />
              ))}
            </ActivityList>
          )}
        </Card>
      </Stack>
    </Paper>
  )
}
